import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Save } from 'lucide-react';
import * as Icons from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const RatingCertificateAssessmentsPage = () => {
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [cards, setCards] = useState([]);
    const [settings, setSettings] = useState({
        section_tag: '',
        section_title: '',
        section_intro: ''
    });

    const [activeTab, setActiveTab] = useState('settings');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCard, setEditingCard] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    const [modalData, setModalData] = useState({
        agency_name: '',
        agency_subtext: '',
        instrument: '',
        rating_value: '',
        rating_outlook: '',
        rating_date: '',
        description: '',
        pdf_link: '',
        icon_name: 'Award',
        gradient_from: 'blue-500',
        gradient_to: 'blue-700',
        display_order: 0,
        is_active: true
    });

    const iconOptions = ['Award', 'Star', 'Shield', 'BarChart', 'TrendingUp'];
    const gradientOptions = [
        { label: 'Blue', from: 'blue-500', to: 'blue-700' },
        { label: 'Purple', from: 'purple-500', to: 'purple-700' },
        { label: 'Orange', from: 'orange-500', to: 'orange-700' },
        { label: 'Green', from: 'green-500', to: 'green-700' },
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, cardsRes] = await Promise.all([
                axios.get('/api/admin/rating-assessments-settings'),
                axios.get('/api/admin/rating-assessments-cards')
            ]);

            if (settingsRes.data.success) {
                setSettings(settingsRes.data.data || {});
            }
            if (cardsRes.data.success) {
                setCards(cardsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            await axios.put('/api/admin/rating-assessments-settings', settings);
            toast.success('Settings saved');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleAddNew = () => {
        setEditingCard(null);
        setModalData({
            agency_name: '',
            agency_subtext: '',
            instrument: '',
            rating_value: '',
            rating_outlook: '',
            rating_date: '',
            description: '',
            pdf_link: '',
            icon_name: 'Award',
            gradient_from: 'blue-500',
            gradient_to: 'blue-700',
            display_order: cards.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (card) => {
        setEditingCard(card);
        setModalData({
            agency_name: card.agency_name,
            agency_subtext: card.agency_subtext || '',
            instrument: card.instrument,
            rating_value: card.rating_value,
            rating_outlook: card.rating_outlook || '',
            rating_date: card.rating_date,
            description: card.description,
            pdf_link: card.pdf_link,
            icon_name: card.icon_name,
            gradient_from: card.gradient_from,
            gradient_to: card.gradient_to,
            display_order: card.display_order,
            is_active: card.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/rating-assessments-cards/${deleteConfirm.id}`);
            toast.success('Card deleted');
            fetchData();
        } catch (error) {
            console.error('Error deleting card:', error);
            toast.error('Failed to delete card');
        } finally {
            setDeleteConfirm({ isOpen: false, id: null });
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCard) {
                await axios.put(`/api/admin/rating-assessments-cards/${editingCard.id}`, modalData);
                toast.success('Card updated');
            } else {
                await axios.post('/api/admin/rating-assessments-cards', modalData);
                toast.success('Card added');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving card:', error);
            toast.error('Failed to save card');
        }
    };

    const renderIcon = (iconName, className = "h-6 w-6") => {
        const IconComponent = Icons[iconName] || Icons.Award;
        return <IconComponent className={className} />;
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-8">
            <h1 className="text-2xl font-bold text-slate-800">Independent Assessments</h1>

            <div className="flex gap-4 border-b border-gray-200">
                <button
                    className={`pb-2 px-4 ${activeTab === 'settings' ? 'border-b-2 border-primary-600 font-bold text-primary-600' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('settings')}
                >
                    Settings
                </button>
                <button
                    className={`pb-2 px-4 ${activeTab === 'cards' ? 'border-b-2 border-primary-600 font-bold text-primary-600' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('cards')}
                >
                    Credit Rating Cards
                </button>
            </div>

            {activeTab === 'settings' && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <form onSubmit={saveSettings} className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                            <input
                                type="text"
                                name="section_tag"
                                value={settings.section_tag}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-xl"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                            <input
                                type="text"
                                name="section_title"
                                value={settings.section_title}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-xl"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Section Intro</label>
                            <textarea
                                name="section_intro"
                                value={settings.section_intro}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-xl"
                                rows={4}
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={savingSettings}
                                className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700"
                            >
                                <Save size={18} />
                                {savingSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {activeTab === 'cards' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <button
                            onClick={handleAddNew}
                            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700"
                        >
                            <Plus size={18} /> Add New Card
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {cards.map((card) => (
                            <div key={card.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gray-100 text-${card.gradient_from}`}>
                                    {renderIcon(card.icon_name)}
                                </div>
                                <h3 className="font-bold text-slate-800 mb-1">{card.agency_name}</h3>
                                {card.agency_subtext && <p className="text-xs text-gray-500 mb-2">{card.agency_subtext}</p>}
                                <div className="text-sm text-gray-600 mb-2">
                                    <p><strong>Rating:</strong> {card.rating_value} {card.rating_outlook && `(${card.rating_outlook})`}</p>
                                    <p><strong>Date:</strong> {card.rating_date}</p>
                                </div>
                                <div className="flex justify-between items-center pt-4 border-t mt-4">
                                    <span className={`text-xs font-medium px-2 py-1 rounded-lg ${card.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                        {card.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleEdit(card)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={16} /></button>
                                        <button onClick={() => handleDelete(card.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                        <h2 className="text-xl font-bold mb-6">{editingCard ? 'Edit Card' : 'Add New Card'}</h2>
                        <form onSubmit={handleModalSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Agency Name</label>
                                    <input type="text" value={modalData.agency_name} onChange={(e) => setModalData({ ...modalData, agency_name: e.target.value })} className="w-full px-4 py-2 border rounded-xl" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Subtext (Optional)</label>
                                    <input type="text" value={modalData.agency_subtext} onChange={(e) => setModalData({ ...modalData, agency_subtext: e.target.value })} className="w-full px-4 py-2 border rounded-xl" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Instrument</label>
                                    <input type="text" value={modalData.instrument} onChange={(e) => setModalData({ ...modalData, instrument: e.target.value })} className="w-full px-4 py-2 border rounded-xl" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Rating Value</label>
                                    <input type="text" value={modalData.rating_value} onChange={(e) => setModalData({ ...modalData, rating_value: e.target.value })} className="w-full px-4 py-2 border rounded-xl" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Rating Outlook</label>
                                    <input type="text" value={modalData.rating_outlook} onChange={(e) => setModalData({ ...modalData, rating_outlook: e.target.value })} className="w-full px-4 py-2 border rounded-xl" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Date</label>
                                    <input type="text" value={modalData.rating_date} onChange={(e) => setModalData({ ...modalData, rating_date: e.target.value })} className="w-full px-4 py-2 border rounded-xl" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Description</label>
                                <textarea value={modalData.description} onChange={(e) => setModalData({ ...modalData, description: e.target.value })} className="w-full px-4 py-2 border rounded-xl" rows={3} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">PDF Link</label>
                                <input type="text" value={modalData.pdf_link} onChange={(e) => setModalData({ ...modalData, pdf_link: e.target.value })} className="w-full px-4 py-2 border rounded-xl" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Icon</label>
                                    <select value={modalData.icon_name} onChange={(e) => setModalData({ ...modalData, icon_name: e.target.value })} className="w-full px-4 py-2 border rounded-xl">
                                        {iconOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Color Theme</label>
                                    <select value={modalData.gradient_from} onChange={(e) => {
                                        const theme = gradientOptions.find(g => g.from === e.target.value);
                                        setModalData({ ...modalData, gradient_from: theme.from, gradient_to: theme.to });
                                    }} className="w-full px-4 py-2 border rounded-xl">
                                        {gradientOptions.map(opt => <option key={opt.from} value={opt.from}>{opt.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Display Order</label>
                                    <input type="number" value={modalData.display_order} onChange={(e) => setModalData({ ...modalData, display_order: parseInt(e.target.value) })} className="w-full px-4 py-2 border rounded-xl" />
                                </div>
                                <div className="flex items-center pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={modalData.is_active} onChange={(e) => setModalData({ ...modalData, is_active: e.target.checked })} className="w-5 h-5 rounded text-primary-600" />
                                        <span className="font-medium">Active</span>
                                    </label>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-2 border rounded-xl hover:bg-slate-50">Cancel</button>
                                <button type="submit" className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <ConfirmDialog isOpen={deleteConfirm.isOpen} onClose={() => setDeleteConfirm({ isOpen: false, id: null })} onConfirm={confirmDelete} title="Delete Card" message="Are you sure?" />
        </div>
    );
};

export default RatingCertificateAssessmentsPage;
